import { Box, Center, Flex } from '@chakra-ui/react';
import { Form, Formik } from 'formik';

import ButtonedRadioGroup from '../../../components/ButtonedRadioGroup';
import { useLEAccordionItemContext } from '../../../components/LEAccordion/LEAccordionItem/useLEAccordionItem';
import TextWithTooltip from '../../../components/TextWithTooltip';
import { Label, SSNInput } from '../../../components/formComponents';
import DateInput from '../../../components/formComponents/DateInput';
import PrimaryButton from '../../../components/ui/buttons/PrimaryButton';
import { getTempInfoCountryConfig } from '../../../constants/countries';
import { options } from '../../../constants/maritalStatuses';
import {
  RelationToBuyerEnum,
  StateAbbreviation,
  useTemporaryInfoSsnUpdateMutation,
  useTemporaryInfoUpdateMutation,
} from '../../../gql/generated/graphql';
import { usePrequalification, useStep, useTempInfo } from '../../../hooks';
import { trackFlowFinish } from '../../../services/analytics/conversionEvents';
import {
  CobuyerSsnAndBirthdayValues,
  cobuyerSsnAndBirthdayInitialValues,
  cobuyerSsnAndBirthdayValidationSchema,
} from '../utils';

const CobuyerSsnAndBirthdate = () => {
  const [updateTemporaryInfo] = useTemporaryInfoUpdateMutation();
  const [temporaryInfoSsnUpdate] = useTemporaryInfoSsnUpdateMutation();

  const { updateState, isExpanded } = useLEAccordionItemContext();
  const step = useStep();
  const { info } = useTempInfo({ fetchPolicy: 'network-only', refresh: isExpanded });
  const tempInfoData = info?.data;
  const { creditPull } = getTempInfoCountryConfig(tempInfoData);

  const {
    shouldExecutePrequalification,
    isSubmitStep,
    consentLoading,
    termsLoading,
    executePrequalification,
    savePrivacyConsents,
    ConsentContent,
    TermsAndConditionsModal,
  } = usePrequalification({
    skip: !!tempInfoData?.prequal_id,
  });

  const onSubmitForm = async (values: CobuyerSsnAndBirthdayValues) => {
    updateState({ status: 'loading' });

    if (creditPull) {
      await temporaryInfoSsnUpdate({
        variables: {
          tempInfoId: info?.id ?? '',
          ssn: values?.cobuyer_ssn,
          isCobuyer: true,
        },
      });
    }

    const updatedTempInfo = await updateTemporaryInfo({
      variables: {
        info: {
          id: info?.id,
          data: {
            cobuyer_dob: values?.cobuyer_dob,
            cobuyer_marital_status: values?.cobuyer_marital_status,
          },
        },
      },
    });

    if (!(await savePrivacyConsents())) {
      updateState({ status: 'error' });
      return;
    }

    await executePrequalification(updatedTempInfo.data?.temporaryInfoUpdate?.data);

    updateState({ status: 'complete' });

    trackFlowFinish(info?.id);

    step.moveNext();
  };

  const showMaritalStatusSelector =
    tempInfoData?.cobuyer_relation_to_buyer !== RelationToBuyerEnum.Spouse &&
    tempInfoData?.cobuyer_state === StateAbbreviation.Wi;

  if (!tempInfoData) {
    return null;
  }

  return (
    <Box>
      <Center mb={{ base: 4, md: 0 }}>
        <TextWithTooltip label="Providing this information helps us fulfill lender requirements to verify identities.">
          Why do we need this?
        </TextWithTooltip>
      </Center>
      <Formik
        enableReinitialize
        initialValues={cobuyerSsnAndBirthdayInitialValues(tempInfoData)}
        validationSchema={cobuyerSsnAndBirthdayValidationSchema(
          showMaritalStatusSelector,
          creditPull,
        )}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={onSubmitForm}
      >
        {({ isSubmitting, values, setFieldValue }) => {
          return (
            <Form>
              <Flex
                flexDir={{ base: 'column', md: 'row' }}
                alignItems="flex-start"
                my={4}
                mx="auto"
                w={{ base: '100%', md: '90%', lg: '95%', xl: '100%' }}
              >
                <DateInput
                  _container={{ mr: { base: 0, md: 2 }, mb: { base: 4, md: 0 } }}
                  hideCalendar
                  label="BIRTHDATE"
                  name="cobuyer_dob"
                />
                {creditPull && (
                  <SSNInput
                    _container={{ ml: { base: 0, md: 2 }, mb: { base: 4, md: 0 } }}
                    name="cobuyer_ssn"
                    digits="9"
                    label="SOCIAL SECURITY NUMBER"
                  />
                )}
              </Flex>
              {showMaritalStatusSelector && (
                <Box mt={4} mb={10}>
                  <Label mb={2}>MARITAL STATUS</Label>
                  <ButtonedRadioGroup
                    columns={{ base: 2, md: 4 }}
                    value={values.cobuyer_marital_status}
                    onChange={(value: string) => setFieldValue('cobuyer_marital_status', value)}
                    options={options}
                    name="cobuyer_marital_status"
                  />
                </Box>
              )}
              {ConsentContent}
              <Center>
                <PrimaryButton
                  trackClick={
                    shouldExecutePrequalification
                      ? 'cobuyer_ssn_and_birthday_consent_and_submit'
                      : 'cobuyer_ssn_and_birthday_submit'
                  }
                  isDisabled={isSubmitting || consentLoading || termsLoading}
                  mt="30px"
                  mb={{
                    base: '20px',
                    lg: '0',
                  }}
                  loading={isSubmitting}
                  type="submit"
                >
                  {isSubmitStep ? 'SUBMIT' : 'NEXT'}
                </PrimaryButton>
              </Center>
            </Form>
          );
        }}
      </Formik>

      {TermsAndConditionsModal}
    </Box>
  );
};

export default CobuyerSsnAndBirthdate;
